import {
  deleteWithAuth,
  fetchWithAuth,
  getWithAuth,
  parseApiError,
  patchWithAuth,
  postWithAuth,
  putWithAuth,
} from 'utils/api-client';
import type { CardDesign } from 'services/qrCard/cardDesign';
import type { InvitationSettings } from './invitation';

export interface StoryBlock {
  key:
    | 'where-it-all-began'
    | 'a-love-takes-shape'
    | 'a-promise-between-them'
    | 'the-beginning-of-forever';
  year?: string;
  content?: string;
  // Presigned URL of the couple-uploaded slide photo, or "" when the block
  // uses the default story-{idx+1}.jpg on the invite. The PUT /api/couple/event
  // endpoint preserves this server-side when only text fields are sent, so the
  // text-editing flow doesn't need to round-trip the imageUrl.
  imageUrl?: string;
}

export const STORY_BLOCK_KEYS = [
  'where-it-all-began',
  'a-love-takes-shape',
  'a-promise-between-them',
  'the-beginning-of-forever',
] as const;

export type StoryBlockKey = (typeof STORY_BLOCK_KEYS)[number];

export interface ScheduleEntry {
  time: string;
  title: string;
  description?: string;
}

export type CouplePreferredLanguage = 'el' | 'en';

export interface CoupleDataResponse {
  couple: {
    id: string;
    email: string;
    eventId: string;
    displayName: string;
    passwordChangedAt?: string;
    isEmailVerified?: boolean;
    preferredLanguage?: CouplePreferredLanguage;
  };
  event: {
    eventId: string;
    name: string;
    // Event type — defaults to "wedding" for events created before the field
    // existed. Drives event-type-aware UI (e.g. hiding the guest "Side" filter
    // for baptisms). See app/src/utils/eventType.ts.
    eventType?: 'wedding' | 'baptism' | 'wedding_baptism';
    // Baptized child (baptism / wedding_baptism). Empty strings for weddings.
    baby?: { firstName?: string; lastName?: string };
    dateISO: string;
    venueName?: string;
    venueMapUrl?: string;
    // Presigned download URL for the couple-uploaded reception photo, or "" when
    // the couple hasn't customized this slot. Server resolves the stored S3 key
    // to a fresh 5-minute presigned URL on each `/me` fetch.
    receptionImage?: string;
    churchLocation?: string;
    churchMapUrl?: string;
    // Presigned download URL for the couple-uploaded ceremony photo (see above).
    ceremonyImage?: string;
    // Couple-uploaded invitation hero photo (top of invite). Empty = use default.
    mainImage?: string;
    // Couple-uploaded "keepsake" photo rendered next to participant names.
    // Generic by design — couples can put whatever feels right. Empty = use default.
    participantImage?: string;
    // "The Moments" gallery teaser section on the invite. 1 arc-frame + 3 cards.
    // Each is a presigned download URL or "" when using the bundled default.
    momentsArcImage?: string;
    momentsCard1Image?: string;
    momentsCard2Image?: string;
    momentsCard3Image?: string;
    // Couple-uploaded hero for the guest join page. Unlike the invitation
    // photos above, this is available on every plan. Empty = use the bundled
    // event-type default.
    joinHeroImage?: string;
    location?: string;
    plan: 'free' | 'basic' | 'pro';
    // The disposable "try the full plan" demo, read from the event.
    isSandbox?: boolean;
    maxBytes: number;
    usedBytes: number;
    hasJoinCode: boolean;
    joinCode: string;
    galleryVisibility: string;
    allowGuestDownload: boolean;
    requireGuestEmail: boolean;
    reactionsAndCommentsEnabled: boolean;
    // "Find your seat" guest lookup (Heirloom). Master toggle + the sub-toggle
    // that reveals other guests' first names at the table.
    seatingLookupEnabled?: boolean;
    seatingLookupShowTablemates?: boolean;
    rsvpDeadline?: string | null;
    // The couple's saved printable QR card (see services/qrCard). Text fields
    // are null when they follow the event.
    qrCard?: SavedQrCard;
    // The invitation editor's settings: template, sections turned off, lines the
    // couple rewrote (see services/invitation). Absent on an older API.
    invitation?: InvitationSettings;
    subscriptionStatus: string;
    validUntil?: string;
    createdAt: string;
    // Whether the couple has a published quiz. Drives the mobile bottom-nav
    // post-event layout (Quiz tab vs. Insights fallback). See MobileBottomNav.
    hasQuiz?: boolean;
    partner1?: {
      firstName: string;
      lastName: string;
      phone: string;
    };
    partner2?: {
      firstName: string;
      lastName: string;
      phone: string;
    };
    // Legacy story fields (kept for backward compat)
    ourStory?: string;
    proposalStory?: string;
    weddingVision?: string;
    // Structured story blocks
    storyBlocks?: StoryBlock[];
    // Families & honor attendants
    groomFamily?: string[];
    brideFamily?: string[];
    bestMen?: string[];
    maidsOfHonor?: string[];
    // Schedule of the day
    scheduleEntries?: ScheduleEntry[];
    // Gifts & registry
    giftMessage?: string;
    giftIban?: string;
    giftIbanHolder?: string;
    giftRegistryUrl?: string;
  };
  invitationReadiness: {
    progress: number;
    isReadyToSend: boolean;
    sections: Record<string, { complete: boolean; mandatory: boolean; weight: number }>;
  };
  attendeeCap: {
    count: number;
    cap: number | null;
    exceeded: boolean;
    plan: 'free' | 'basic' | 'pro';
  };
  /**
   * The event's plan limits as the API resolved them — grandfathered per event
   * (api/constants/planLimits.mjs). Absent from an API older than that change.
   */
  planLimits?: {
    maxGuests: number | null;
    maxPhotos: number | null;
    videoUploads: boolean;
  };
  /**
   * Gallery photos vs the plan's photo cap (`cap` null = no cap). `state` is
   * the API's verdict (photoCapState): the dashboard never applies the warning
   * threshold itself. Absent from an older API.
   */
  photoCap?: {
    count: number;
    cap: number | null;
    state?: 'full' | 'near' | null;
  };
  /**
   * The album's two dates as calendar days (YYYY-MM-DD; api/services/albumDates.mjs):
   * the day guests can start uploading, and the last day it can be opened
   * (null when it never closes, as in the sandbox). Absent from an older API.
   */
  albumDates?: {
    uploadOpensOn: string | null;
    accessLastDay: string | null;
  };
  metrics: {
    photoCount: number;
    videoCount: number;
    guestCount: number;
    recentUploads: number;
    guestActivity: {
      active: number;
      joined: number;
      notJoined: number;
    };
    recentActivity: Array<{
      uploaderId: string;
      displayName: string;
      albumId: string | null;
      albumName: string | null;
      albumCreatedInSession: boolean;
      photoCount: number;
      videoCount: number;
      totalFiles: number;
      createdAt: string;
    }>;
    photoBytesUsed: number;
    videoBytesUsed: number;
    topContributors: Array<{
      uploaderId: string;
      displayName: string;
      photoCount: number;
    }>;
  };
}

/**
 * Fetch full couple and event data including plan, storage, and metrics
 * Requires authentication token
 */
export async function getCoupleData(): Promise<CoupleDataResponse> {
  const response = await getWithAuth('/api/couple/me');

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to fetch couple data');
  }

  return response.json();
}

/**
 * Upgrade couple's plan (Free → Basic/Pro or Basic → Pro)
 * Returns Stripe checkout URL
 *
 * THE SINGLE live paid-checkout choke point: PricingCard, PricingCardWide and
 * VerifyEmail all reach Stripe through this one function, so what it sends is
 * what every purchase sends.
 *
 * `credentials: 'include'` is opted into HERE rather than in `fetchWithAuth`,
 * deliberately. Both cookies the purchase analytics need — the GA4 `_ga` client
 * id and the `agapae-consent` record — are scoped to `.agapae.gr` and would ride
 * this same-site request automatically, but only if cookies are sent at all.
 * Making `fetchWithAuth` send them globally would change the CSRF posture of
 * every authenticated request in the app for the benefit of one endpoint; this
 * gets the same result with a blast radius of one call. The server reads both
 * cookies itself (api/services/attribution/consent.mjs,
 * api/services/analytics/gaClientId.mjs) — neither value is sent in the body,
 * and neither is trusted for authorization or for any payment decision.
 *
 * See docs/analytics/purchase-identity.md.
 */
export async function upgradePlan(targetPlan: 'basic' | 'pro'): Promise<{ url: string }> {
  const response = await postWithAuth(
    '/api/couple/upgrade',
    { targetPlan },
    {
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to create upgrade checkout');
  }

  return response.json();
}

export interface UpdateEventRequest extends Partial<
  Omit<CoupleDataResponse['event'], 'eventId' | 'createdAt'>
> {
  newJoinCode?: boolean;
  enableJoinCode?: boolean;
  disableJoinCode?: boolean;
  customJoinCode?: string;
}

/**
 * Update event settings (name, date, locations, privacy, etc.)
 */
export async function updateEventSettings(data: UpdateEventRequest): Promise<void> {
  const response = await putWithAuth('/api/couple/event', data);

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to update settings');
  }
}

/** The printable QR card as stored on the event. Mirrors api/constants/qrCard.mjs. */
export interface SavedQrCard {
  headingScript: string | null;
  headingCaps: string | null;
  instructions: string | null;
  names: string | null;
  date: string | null;
  size: string;
  showLogo: boolean;
  /** The "made with Agapaé" line. Always on for Blossom — the API enforces it. */
  showCredit?: boolean;
  /** Fonts, case, paper/frame/divider and each item's size, position and spacing — only what the couple changed from their preset. */
  design?: CardDesign;
  /** The ready-made look the card starts from; none means the default one. */
  preset?: string | null;
  /** The couple's own template the look was taken from, while the card is on it. */
  template?: string | null;
  /** The language its suggested lines and date are written in; none follows the dashboard's. */
  language?: 'el' | 'en' | null;
  /** Looks the couple saved to choose again. Managed by their own calls below, never by a card save. */
  templates?: SavedQrCardTemplate[];
  /**
   * The couple's version of each ready-made template they changed, by preset —
   * the API keeps these in step with the card (it never takes them in a save).
   */
  presetEdits?: Partial<Record<string, QrCardTemplateWording & { design: CardDesign }>>;
}

/**
 * A card a couple saved to choose again: the preset it was made from, their
 * changes on top of it, and the lines they wrote (null follows the defaults).
 */
export interface SavedQrCardTemplate extends QrCardTemplateWording {
  id: string;
  name: string;
  preset: string;
  design: CardDesign;
}

/** A template's wording, as on the card: the couple's own line, or null to follow the defaults. */
export interface QrCardTemplateWording {
  headingScript?: string | null;
  headingCaps?: string | null;
  instructions?: string | null;
  names?: string | null;
  date?: string | null;
}

/** What a template keeps, saved whole: its look and its wording. */
export type QrCardTemplateContent = QrCardTemplateWording & { preset: string; design: CardDesign };

/** The reply to a template change: the full list, and the message to confirm it with. */
export interface QrCardTemplatesReply {
  messageCode?: number;
  message?: string;
  templates: SavedQrCardTemplate[];
}

/** Save the card as one of the couple's own templates (or copy one of theirs). */
export async function createQrCardTemplate(
  input: QrCardTemplateContent & { name: string },
): Promise<QrCardTemplatesReply & { template: SavedQrCardTemplate }> {
  const response = await postWithAuth('/api/couple/event/qr-card/templates', input);
  if (!response.ok) {
    throw await parseApiError(response, 'Failed to save the template');
  }
  return response.json();
}

/** Rename one of the couple's templates, or save the card into it (its content, whole). */
export async function updateQrCardTemplate(
  id: string,
  changes: { name: string } | QrCardTemplateContent,
): Promise<QrCardTemplatesReply> {
  const response = await patchWithAuth(
    `/api/couple/event/qr-card/templates/${encodeURIComponent(id)}`,
    changes,
  );
  if (!response.ok) {
    throw await parseApiError(response, 'Failed to update the template');
  }
  return response.json();
}

/** Delete one of the couple's templates. A card on it moves off it, so the card comes back too. */
export async function deleteQrCardTemplate(
  id: string,
): Promise<QrCardTemplatesReply & { qrCard: SavedQrCard }> {
  const response = await deleteWithAuth(
    `/api/couple/event/qr-card/templates/${encodeURIComponent(id)}`,
  );
  if (!response.ok) {
    throw await parseApiError(response, 'Failed to delete the template');
  }
  return response.json();
}

/** Put a ready-made template back as drawn, dropping the couple's changes to it. */
export async function resetQrCardPreset(
  preset: string,
): Promise<{ messageCode?: number; message?: string; qrCard: SavedQrCard }> {
  const response = await deleteWithAuth(
    `/api/couple/event/qr-card/presets/${encodeURIComponent(preset)}`,
  );
  if (!response.ok) {
    throw await parseApiError(response, 'Failed to reset the template');
  }
  return response.json();
}

/**
 * Save the printable QR card. The whole card is sent; null text fields keep
 * following the event.
 */
export async function saveQrCard(card: SavedQrCard): Promise<SavedQrCard> {
  const response = await putWithAuth('/api/couple/event/qr-card', card);

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to save the QR card');
  }
  const body = await response.json();
  return body.qrCard as SavedQrCard;
}

export interface UpdatePasswordRequest {
  currentPassword?: string;
  newPassword?: string;
}

/**
 * Update couple password
 */
export async function updatePassword(
  data: UpdatePasswordRequest,
): Promise<{ token: string; refreshToken: string }> {
  const response = await putWithAuth('/api/couple/password', data);

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to update password');
  }

  return response.json();
}

/**
 * Download a JSON export of all the couple's personal data (GDPR Art. 15 + 20).
 * The server returns a JSON body with a Content-Disposition: attachment header;
 * we save it to disk via an in-memory blob.
 */
export async function downloadMyData(): Promise<void> {
  const response = await fetchWithAuth('/api/couple/me/export');

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to export data');
  }

  // Try to extract filename from Content-Disposition (server sends one),
  // fall back to a reasonable default.
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || `agapae-export-${Date.now()}.json`;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ─────────────────────────────────────────────────────────────────────────
// Invitation photo customization (Heirloom-only)
// ─────────────────────────────────────────────────────────────────────────

// Flat slots target a top-level Event field. Compound `story:<key>` slots
// target the matching storyBlock subdocument (see api/validators.mjs).
export type InvitationPhotoFlatSlot =
  | 'ceremony'
  | 'reception'
  | 'hero'
  | 'keepsake'
  | 'moments-arc'
  | 'moments-card-1'
  | 'moments-card-2'
  | 'moments-card-3'
  // Not an invitation slot: renders on the guest join page, and unlike every
  // other slot here it is available on all plans (api/validators.mjs
  // ALL_PLAN_INVITATION_PHOTO_SLOTS).
  | 'join-hero';
export type InvitationPhotoStorySlot = `story:${StoryBlockKey}`;
export type InvitationPhotoSlot = InvitationPhotoFlatSlot | InvitationPhotoStorySlot;

export const INVITATION_PHOTO_MAX_BYTES = 10 * 1024 * 1024; // 10 MB; mirrors BE

// Mirrors api/validators.mjs INVITATION_PHOTO_MIME_TYPES. Kept in lockstep so
// the FE rejects unsupported files before consuming a presigned URL slot.
export const INVITATION_PHOTO_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

// Some browsers (notably Windows / older Safari) don't register a MIME type
// for HEIC/HEIF photos coming straight from an iPhone — `file.type` ends up
// empty or `application/octet-stream`. Falling back to the extension here
// recovers those uploads without weakening the server-side allowlist (the
// /complete handler still magic-byte verifies the actual stored bytes).
const EXTENSION_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

const inferInvitationPhotoContentType = (file: File): string => {
  const browserType = (file.type || '').toLowerCase();
  if (browserType && INVITATION_PHOTO_MIME_TYPES.includes(browserType)) {
    return browserType;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TO_MIME[ext] || browserType || 'application/octet-stream';
};

const isHeicFile = (file: File): boolean => {
  const inferred = inferInvitationPhotoContentType(file).toLowerCase();
  return inferred === 'image/heic' || inferred === 'image/heif';
};

/**
 * Convert a HEIC/HEIF photo into a JPEG File so it can render in browsers
 * other than Safari. Lazy-imported so the ~70 KB heic2any bundle isn't paid
 * for by couples uploading regular JPEG/PNG photos. Falls back to the
 * original file if conversion fails — the server will still reject it via
 * the magic-byte check at /complete, surfacing a sane error.
 */
const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    const heic2anyModule = await import('heic2any');
    const heic2any = heic2anyModule.default;
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
    // heic2any returns Blob for single-image HEICs and Blob[] for multi-page
    // HEICs (rare — Live Photos). Take the first page in the multi case.
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (err) {
    console.warn('[invitation-photo] HEIC conversion failed, uploading original:', err);
    return file;
  }
};

interface InvitationPhotoStartResponse {
  key: string;
  url: string;
  contentType: string;
}

interface InvitationPhotoCompleteResponse {
  messageCode: number;
  message: string;
  key: string;
}

/**
 * Upload a couple-customized invitation photo. Three-step flow matching the
 * gallery pipeline: server mints a presigned PUT URL → client PUTs the file
 * directly to S3 → server verifies + commits the S3 key to the Event doc.
 *
 * Returns the response from /complete (success message code + the new key).
 * Callers should refetch couple data afterwards so the dashboard's slot
 * preview picks up the new presigned download URL.
 */
export async function uploadInvitationPhoto(
  eventId: string,
  slot: InvitationPhotoSlot,
  file: File,
): Promise<InvitationPhotoCompleteResponse> {
  // 0. HEIC/HEIF can't render in non-Safari browsers, so convert to JPEG
  //    client-side before uploading. Couples shoot in HEIC on iPhones but
  //    their guests (and their own dashboard preview) view on Chrome/Firefox.
  //    Conversion is a no-op for other formats.
  const uploadFile = isHeicFile(file) ? await convertHeicToJpeg(file) : file;

  // 1. Ask the server for a presigned PUT URL + the S3 key we'll commit to.
  //    Compound slots (`story:<key>`) contain `:`, which has no special meaning
  //    in URL path segments but is friendlier to encode for proxies / loggers.
  const slotSegment = encodeURIComponent(slot);
  // Recover the content type from the extension for files where the browser
  // didn't register a MIME (older Safari / Windows + HEIC). Server also
  // magic-byte-checks at /complete.
  const contentType = inferInvitationPhotoContentType(uploadFile);
  const startRes = await postWithAuth(
    `/api/couple/events/${encodeURIComponent(eventId)}/invitation-photo/${slotSegment}/start`,
    {
      fileName: uploadFile.name,
      contentType,
      fileSize: uploadFile.size,
    },
  );
  if (!startRes.ok) {
    throw await parseApiError(startRes, 'Failed to start invitation photo upload');
  }
  const {
    url,
    key,
    contentType: signedContentType,
  }: InvitationPhotoStartResponse = await startRes.json();

  // 2. Upload bytes directly to S3 using the presigned URL. The Content-Type
  //    we send MUST match what was signed by the server, otherwise S3 returns
  //    SignatureDoesNotMatch. We send `uploadFile` (post-HEIC-conversion)
  //    so the bytes match what /start declared.
  const s3Res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': signedContentType },
    body: uploadFile,
  });
  if (!s3Res.ok) {
    throw new Error(`S3 upload failed with status ${s3Res.status}`);
  }

  // 3. Notify the server so it can verify the stored object (size + magic
  //    bytes) and commit the key onto the Event document.
  const completeRes = await postWithAuth(
    `/api/couple/events/${encodeURIComponent(eventId)}/invitation-photo/${slotSegment}/complete`,
    { key },
  );
  if (!completeRes.ok) {
    throw await parseApiError(completeRes, 'Failed to finalize invitation photo upload');
  }
  return completeRes.json();
}

/**
 * Restore a slot's default invitation photo by clearing the couple's custom
 * upload. Best-effort deletes the S3 object server-side.
 */
export async function deleteInvitationPhoto(
  eventId: string,
  slot: InvitationPhotoSlot,
): Promise<{ messageCode: number; message: string }> {
  const response = await deleteWithAuth(
    `/api/couple/events/${encodeURIComponent(eventId)}/invitation-photo/${encodeURIComponent(slot)}`,
  );
  if (!response.ok) {
    throw await parseApiError(response, 'Failed to restore default invitation photo');
  }
  return response.json();
}

export interface DeleteAccountResponse {
  ok: true;
  deletedAt: string;
  subscriptionCancelled: boolean;
}

/**
 * Persist the couple's preferred language for transactional emails.
 * Called by SettingsProvider whenever the user changes the dashboard locale.
 * Fire-and-forget from the caller's perspective — failures are surfaced via
 * the standard error contract but don't block the FE language change.
 */
export async function updateCoupleLanguage(
  preferredLanguage: CouplePreferredLanguage,
): Promise<{ messageCode: number; message: string; preferredLanguage: CouplePreferredLanguage }> {
  const response = await patchWithAuth('/api/couple/preferences/language', {
    preferredLanguage,
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to save language preference');
  }

  return response.json();
}

/**
 * Permanently delete the couple's account, event, and all associated data
 * (GDPR Art. 17). The backend re-verifies the password server-side, so a
 * stolen token alone cannot trigger deletion.
 */
export async function deleteMyAccount(confirmPassword: string): Promise<DeleteAccountResponse> {
  const response = await deleteWithAuth('/api/couple/me', {
    body: JSON.stringify({ confirmPassword }),
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw await parseApiError(response, 'Failed to delete account');
  }

  return response.json();
}
